import { useEffect, useState } from "react"
import { adminApi, type Product, type Category } from "@/lib/admin-api"
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card"
import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import { Badge } from "@/components/ui/badge"
import {
    Table, TableBody, TableCell, TableHead, TableHeader, TableRow
} from "@/components/ui/table"
import {
    Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription
} from "@/components/ui/dialog"
import {
    DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger
} from "@/components/ui/dropdown-menu"
import {
    Plus, Search, MoreVertical, Pencil, Trash2,
    Package, Eye, Loader2
} from "lucide-react"
import { Link, useNavigate } from "react-router-dom"
import { toast } from "sonner"
import { cn } from "@/lib/utils"

export default function AdminProducts() {
    const navigate = useNavigate()
    const [products, setProducts] = useState<Product[]>([])
    const [categories, setCategories] = useState<Category[]>([])
    const [isLoading, setIsLoading] = useState(true)
    const [searchQuery, setSearchQuery] = useState("")
    const [deleteModal, setDeleteModal] = useState<{ open: boolean; product: Product | null }>({
        open: false,
        product: null
    })
    const [isDeleting, setIsDeleting] = useState(false)

    const fetchData = async () => {
        try {
            setIsLoading(true)
            const [productsData, categoriesData] = await Promise.all([
                adminApi.getProducts(),
                adminApi.getCategories()
            ])
            setProducts(productsData)
            setCategories(categoriesData)
        } catch (error) {
            console.error("Failed to fetch data:", error)
            toast.error("Ma'lumotlarni yuklashda xatolik")
        } finally {
            setIsLoading(false)
        }
    }

    useEffect(() => {
        fetchData()
    }, [])

    const handleDelete = async () => {
        if (!deleteModal.product) return

        try {
            setIsDeleting(true)
            await adminApi.deleteProduct(deleteModal.product.id)
            toast.success("Mahsulot o'chirildi")
            setDeleteModal({ open: false, product: null })
            fetchData()
        } catch (error) {
            console.error("Failed to delete:", error)
            toast.error("O'chirishda xatolik")
        } finally {
            setIsDeleting(false)
        }
    }

    const formatPrice = (price: string | number) => {
        return new Intl.NumberFormat("uz-UZ").format(Number(price)) + " so'm"
    }

    const filteredProducts = products.filter(product =>
        product.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
        product.category_name.toLowerCase().includes(searchQuery.toLowerCase())
    )

    if (isLoading) {
        return (
            <div className="flex items-center justify-center min-h-[60vh]">
                <div className="flex flex-col items-center gap-4">
                    <div className="w-12 h-12 border-4 border-emerald-500 border-t-transparent rounded-full animate-spin" />
                    <p className="text-muted-foreground">Yuklanmoqda...</p>
                </div>
            </div>
        )
    }

    return (
        <div className="space-y-6">
            {/* Header */}
            <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
                <div>
                    <h1 className="text-3xl font-bold text-gray-900 dark:text-white">Mahsulotlar</h1>
                    <p className="text-muted-foreground">Jami {products.length} ta mahsulot</p>
                </div>
                <Link to="/admin/products/new">
                    <Button className="bg-gradient-to-r from-emerald-500 to-teal-600 hover:from-emerald-600 hover:to-teal-700 text-white gap-2">
                        <Plus className="h-5 w-5" />
                        Yangi mahsulot
                    </Button>
                </Link>
            </div>

            {/* Search */}
            <Card>
                <CardContent className="p-4">
                    <div className="relative">
                        <Search className="absolute left-4 top-1/2 -translate-y-1/2 h-5 w-5 text-muted-foreground" />
                        <Input
                            placeholder="Mahsulot qidirish..."
                            value={searchQuery}
                            onChange={(e) => setSearchQuery(e.target.value)}
                            className="pl-12 h-12 bg-slate-50 dark:bg-gray-800 border-0"
                        />
                    </div>
                </CardContent>
            </Card>

            {/* Products Table */}
            <Card>
                <CardContent className="p-0">
                    <Table>
                        <TableHeader>
                            <TableRow>
                                <TableHead>Mahsulot</TableHead>
                                <TableHead>Kategoriya</TableHead>
                                <TableHead>Narxi</TableHead>
                                <TableHead>Ombor</TableHead>
                                <TableHead>Holat</TableHead>
                                <TableHead className="w-[100px]"></TableHead>
                            </TableRow>
                        </TableHeader>
                        <TableBody>
                            {filteredProducts.map((product) => (
                                <TableRow key={product.id}>
                                    <TableCell>
                                        <div className="flex items-center gap-3">
                                            {product.image_url ? (
                                                <img
                                                    src={product.image_url}
                                                    alt={product.name}
                                                    className="w-12 h-12 rounded-lg object-cover"
                                                />
                                            ) : (
                                                <div className="w-12 h-12 rounded-lg bg-slate-100 dark:bg-gray-800 flex items-center justify-center">
                                                    <Package className="h-6 w-6 text-muted-foreground" />
                                                </div>
                                            )}
                                            <div>
                                                <p className="font-medium">{product.name}</p>
                                                <p className="text-sm text-muted-foreground truncate max-w-[200px]">
                                                    {product.description.substring(0, 50)}...
                                                </p>
                                            </div>
                                        </div>
                                    </TableCell>
                                    <TableCell>
                                        <Badge variant="outline">{product.category_name}</Badge>
                                    </TableCell>
                                    <TableCell className="font-semibold">
                                        {formatPrice(product.price)}
                                    </TableCell>
                                    <TableCell>
                                        <Badge variant={product.stock > 10 ? "default" : product.stock > 0 ? "secondary" : "destructive"}
                                            className={cn(product.stock > 10 && "bg-emerald-500")}
                                        >
                                            {product.stock} ta
                                        </Badge>
                                    </TableCell>
                                    <TableCell>
                                        <Badge variant={product.available ? "default" : "secondary"}
                                            className={cn(product.available && "bg-emerald-500")}
                                        >
                                            {product.available ? "Faol" : "Nofaol"}
                                        </Badge>
                                    </TableCell>
                                    <TableCell>
                                        <DropdownMenu>
                                            <DropdownMenuTrigger asChild>
                                                <Button variant="ghost" size="icon">
                                                    <MoreVertical className="h-4 w-4" />
                                                </Button>
                                            </DropdownMenuTrigger>
                                            <DropdownMenuContent align="end">
                                                <DropdownMenuItem onClick={() => window.open(`/products/${product.slug}`, "_blank")}>
                                                    <Eye className="h-4 w-4 mr-2" />
                                                    Ko'rish
                                                </DropdownMenuItem>
                                                <DropdownMenuItem onClick={() => navigate(`/admin/products/${product.id}/edit`)}>
                                                    <Pencil className="h-4 w-4 mr-2" />
                                                    Tahrirlash
                                                </DropdownMenuItem>
                                                <DropdownMenuItem
                                                    onClick={() => setDeleteModal({ open: true, product })}
                                                    className="text-red-600 focus:text-red-600"
                                                >
                                                    <Trash2 className="h-4 w-4 mr-2" />
                                                    O'chirish
                                                </DropdownMenuItem>
                                            </DropdownMenuContent>
                                        </DropdownMenu>
                                    </TableCell>
                                </TableRow>
                            ))}
                        </TableBody>
                    </Table>
                    {filteredProducts.length === 0 && (
                        <div className="text-center py-12">
                            <Package className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
                            <p className="text-muted-foreground">Mahsulotlar topilmadi</p>
                        </div>
                    )}
                </CardContent>
            </Card>

            {/* Delete Confirmation Modal */}
            <Dialog open={deleteModal.open} onOpenChange={(open) => setDeleteModal({ open, product: null })}>
                <DialogContent>
                    <DialogHeader>
                        <DialogTitle>Mahsulotni o'chirish</DialogTitle>
                        <DialogDescription>
                            "{deleteModal.product?.name}" mahsulotini o'chirmoqchimisiz? Bu amalni qaytarib bo'lmaydi.
                        </DialogDescription>
                    </DialogHeader>
                    <DialogFooter>
                        <Button variant="outline" onClick={() => setDeleteModal({ open: false, product: null })}>
                            Bekor qilish
                        </Button>
                        <Button variant="destructive" onClick={handleDelete} disabled={isDeleting}>
                            {isDeleting ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : null}
                            O'chirish
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>
        </div>
    )
}
